import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, MenuV2 } from '@figma/fpl-components';
import { Icon24Export, Icon24More, Icon24Trash } from '@figma/fpl-icons';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { I18N_KEYS } from '@/language/keys';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { useResolvedAssets } from '@/state/workflow.state';
import { isDownloadableToolResult, toDownloadableToolResult } from './ToolOutputDownload';
import { getToolOutputName } from './ToolOutputNames';
import type { ToolOutputGroup } from './ToolOutputGroups';

interface ToolOutputBatchActionsProps {
  group: ToolOutputGroup;
  recipeName: string;
  /** Without it, the menu holding Clear is not shown. */
  onRemoveBatch?: (batchId: string) => void;
  disableRemove: boolean;
}

export function ToolOutputBatchActions({
  group,
  recipeName,
  onRemoveBatch,
  disableRemove,
}: ToolOutputBatchActionsProps) {
  const { t } = useTranslation();
  const labels = I18N_KEYS.SHARED_DESIGN_APP.OUTPUT_HISTORY;
  const { downloadNamedAssetsWithProgress } = useInteractiveDownloader();
  const resolvedAssets = useResolvedAssets();
  const assets = group.results
    .filter(({ result }) => isDownloadableToolResult(result, resolvedAssets))
    .map(({ result }) => ({
      name: getToolOutputName(result, t),
      asset: toDownloadableToolResult(result),
    }));
  const showDownloadButton = assets.length > 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const { manager, getTriggerProps } = MenuV2.useMenu({ open: menuOpen, onOpenChange: setMenuOpen });
  const batchId = group.batchId;
  const canRemove =
    Boolean(batchId) && group.results.every(({ result }) => typeof result.id === 'string' && Boolean(result.id));

  return (
    <div {...stylex.props(styles.actions)}>
      {/* A section can hold only loading or failed cards, which have nothing to download. */}
      {showDownloadButton && (
        <Button
          variant="secondary"
          size="lg"
          iconPrefix={<Icon24Export />}
          onClick={() => {
            void downloadNamedAssetsWithProgress({ assets, archiveName: recipeName });
          }}
        >
          {t(I18N_KEYS.GENERAL.DOWNLOAD)}
        </Button>
      )}
      {onRemoveBatch && group.results.length > 0 && (
        <>
          <IconButton aria-label={t(labels.MORE_BATCH)} {...getTriggerProps()}>
            <Icon24More />
          </IconButton>
          <MenuV2.Root
            manager={manager}
            position={{ area: 'bottom span-left' }}
            colorTheme={{ brand: 'weave', mode: 'dark' }}
          >
            <MenuV2.Item
              variant="destructive"
              disabled={disableRemove || !canRemove}
              aria-description={canRemove ? undefined : t(labels.REMOVE_UNAVAILABLE)}
              lead={<Icon24Trash />}
              onClick={() => {
                if (batchId && canRemove && !disableRemove) onRemoveBatch(batchId);
              }}
            >
              <span title={canRemove ? undefined : t(labels.REMOVE_UNAVAILABLE)}>{t(I18N_KEYS.GENERAL.CLEAR)}</span>
            </MenuV2.Item>
          </MenuV2.Root>
        </>
      )}
    </div>
  );
}

const styles = stylex.create({
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacer.spacer8px,
    marginInlineStart: 'auto',
  },
});
